'use client'

import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  syncReadinessIssuePresentation,
  type AccountRowModel,
} from '@/lib/sync-readiness-rows'

export function SyncReadinessIssue({
  row,
  orgSlug,
  brandSlug,
  canManageRecovery,
  retrying,
  retryAccepted,
  onRetry,
  showSupportLink = true,
}: {
  row: AccountRowModel
  orgSlug: string
  brandSlug: string
  canManageRecovery: boolean
  retrying: boolean
  retryAccepted: boolean
  onRetry: (row: AccountRowModel) => void
  showSupportLink?: boolean
}) {
  const t = useTranslations('Common.syncWidget')
  const tIntegrations = useTranslations('SettingsPages.integrations')
  const presentation = syncReadinessIssuePresentation({
    row,
    orgSlug,
    brandSlug,
  })
  if (!presentation) return null

  const retryInFlight = retrying || retryAccepted

  return (
    <div className="space-y-1.5">
      <p className="text-muted-foreground text-[11px]">
        {t(presentation.messageKey)}
      </p>
      {/* `contactAdmin` offers to reconnect or retry, which is exactly what
          neither provider-side hold accepts — the message above already says
          what has to happen outside Soku. */}
      {!canManageRecovery &&
        presentation.action?.kind !== 'grant_access' &&
        presentation.action?.kind !== 'wait' && (
          <p className="text-muted-foreground text-[11px]">
            {t('contactAdmin')}
          </p>
        )}
      {canManageRecovery && presentation.action?.kind === 'reconnect' && (
        <Button asChild size="xs" variant="outline">
          <Link href={presentation.action.href}>
            {tIntegrations('manageDialog.reconnect')}
          </Link>
        </Button>
      )}
      {canManageRecovery && presentation.action?.kind === 'configure' && (
        <Button asChild size="xs" variant="outline">
          <Link href={presentation.action.href}>{t('reviewSetup')}</Link>
        </Button>
      )}
      {/* The Google Ads help link is a public external page, and granting
          account access is done by whoever owns that Google Ads account — not
          by a Soku org admin. Non-admins get the link too. */}
      {presentation.action?.kind === 'grant_access' && (
        <Button asChild size="xs" variant="outline">
          <a
            href={presentation.action.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('googleAccessHelp')}
          </a>
        </Button>
      )}
      {canManageRecovery && presentation.action?.kind === 'retry' && (
        <Button
          size="xs"
          variant="outline"
          disabled={retryInFlight}
          onClick={() => onRetry(row)}
        >
          {retryInFlight && <Loader2 className="size-3 animate-spin" />}
          {retryInFlight ? t('pillSyncing', { count: 1 }) : t('retry')}
        </Button>
      )}
      {showSupportLink && (
        <Link
          className="text-muted-foreground block text-[11px] underline-offset-2 hover:underline"
          href="/contact"
        >
          {t('contactSupport')}
        </Link>
      )}
    </div>
  )
}
